import { useResizeObserver } from '@op/react-web'
import { useEffect } from 'react'

type LineClampProps = {
	readonly children: string
	readonly className?: string
	readonly ellipsis?: string
	readonly maxLines?: number
}

export const LineClamp = ({
	children: text,
	className,
	ellipsis = '…',
	maxLines = 3,
}: LineClampProps) => {
	const [$paragraphElCbRef, $paragraphElRef] = useResizeObserver<HTMLParagraphElement>(() => {
		clampLines($paragraphElRef.current, text, maxLines, ellipsis)
	})

	// Recheck if line clamp is required if text, ellipsis, or max lines change
	useEffect(() => {
		clampLines($paragraphElRef.current, text, maxLines, ellipsis)
	}, [className, ellipsis, maxLines, text])

	return (
		<p className={className} ref={$paragraphElCbRef} title={text}>
			{text}
		</p>
	)
}

let probe: HTMLSpanElement | null = null

const getLineHeight = ($el: HTMLParagraphElement) => {
	const lh = Number.parseFloat(window.getComputedStyle($el).lineHeight)
	if (!Number.isNaN(lh)) return lh

	// Fallback for line-height: normal
	probe ||= document.createElement('span')
	probe.textContent = 'M'
	$el.appendChild(probe)
	const h = probe.getBoundingClientRect().height
	probe.remove()
	return h
}

/**
 * Clamps the text to the given number of lines.
 * This function uses a binary search to find the optimal number of characters to clamp.
 *
 * NOTE: This function contains allowances for the poorly-rendered 'texgyreadventor` font that
 *  we use. This font, unlike all others I have tested, does not render the same `scrollHeight`
 *  and `clientHeight` for a completely unconstrained block of text. In normal cases, an
 *  unconstrained block of text will render exactly the height. Because of this, we subtract 2
 *  from the `scrollHeight` to account for this. We also do `Math.ceil` on the max height, but
 *  this may not be specific to this font (I didn't test).
 */
const clampLines = (
	$el: HTMLParagraphElement | null,
	fullText: string,
	lines: number,
	ellipsis: string,
) => {
	if (!$el) return

	$el.textContent = fullText
	const lh = getLineHeight($el)
	const maxHeight = Math.ceil(lh * lines)

	if ($el.scrollHeight - 2 <= maxHeight) return

	let low = 0
	let high = fullText.length

	while (low < high) {
		const mid = (low + high + 1) >> 1
		$el.textContent = fullText.slice(0, mid) + ellipsis
		if ($el.scrollHeight - 2 <= maxHeight) low = mid
		else high = mid - 1
	}

	$el.textContent = fullText.slice(0, low) + ellipsis
}
